import { DataRecord, RecordKind } from './record.js';
import { SchemaTypeRef } from './schema.js';
import { ExpressionConstraint } from './constraint.js';
import { optionIdSymbol, typeSymbol } from './engine-impl.js';

export type AllOptionsFromType = 'ALL';
export type SourcedValueOption = Option | OptionGroup;
export type SourcedValueOptions = SourcedValueOption[];
export type OptionId = string;
export type OptionGroupId = string;

export class Option<T = { [k: string]: any }> extends DataRecord {
  public model: T;

  constructor(_class: SchemaTypeRef, model: any, id?: OptionId,) {
    super(_class, RecordKind.Option, id);
    this.model = model;
    if (id) {
      (this.model as any)[optionIdSymbol] = id;
    }
    //Set symbols
    this.model[typeSymbol] = _class.typeRef;
  }
}

export class OptionWithSources extends Option {
  public sources: { [k: string]: SourcedValue | ListSourcedValue; };

  constructor(_class: SchemaTypeRef, model: any, sources: { [k: string]: SourcedValue | ListSourcedValue; }, id?: string) {
    for (let sv of Object.keys(sources)) {
      if (sources[sv] instanceof SourcedValue) {
        if (Array.isArray(sources[sv].value)) {
          model[sv] = (sources[sv].value as Option[]).map(v => v.model)
        } else {
          model[sv] = (sources[sv].value as Option).model
        }
      } else {
        model[sv] = sources[sv].value;
      }
    }

    super(_class, model, id);
    this.sources = sources;
  }
}

export class OptionGroup<Model = any, Child = any> extends DataRecord {
  public model: Model;
  // TODO: can have nested OptionGroups, but types fail
  children: Array<Option<Child>>;

  constructor(_class: SchemaTypeRef, children: Array<Option<Child>>, model: any, id?: OptionGroupId) {
    super(_class, RecordKind.OptionGroup, id);
    this.children = children;
    this.model = model;
  }
}

export interface Sourceable {
  getSource(p1?: string): Array<Option | OptionGroup>

  getOptionAvailability(p1?: string): ExpressionConstraint[] | undefined;
}

export class ListSourcedValue implements Sourceable {
  public value: any[];
  public sources: {
    [k: string]: Sourceable;
  };

  // todo: do we need to be Sourceable?
  // public source: Array<Option | OptionGroup> | AllOptionsFromType = [];
  // public optionAvailability: ExpressionConstraint[] | undefined = undefined;

  constructor(value: any[], sources: { [p: string]: Sourceable }) {
    this.value = value;
    this.sources = sources;
  }

  getSource(p1: string): SourcedValueOptions {
    return this.sources[p1] ? this.sources[p1].getSource() : [];
  }

  getOptionAvailability(p1: string): ExpressionConstraint[] | undefined {
    return this.sources[p1] && this.sources[p1].getOptionAvailability();
  }

}

export class Source implements Sourceable {
  protected source: SourcedValueOptions;
  private optionAvailability?: ExpressionConstraint[] | undefined;

  constructor(source: SourcedValueOptions, optionAvailability?: ExpressionConstraint[]) {
    this.source = source;
    this.optionAvailability = optionAvailability;
  }

  public getSource(p1?: string): SourcedValueOptions {
    return this.source;
  }

  public getOptionAvailability(p1: string): ExpressionConstraint[] | undefined {
    return this.optionAvailability;
  }
}

export type SourcedValueDefinition = string[] | { match: any };

export class SourcedValue extends Source {
  public value: Option | Option[];

  constructor(value: Option | Option[],
              source: SourcedValueOptions | null = null,
              optionAvailability?: ExpressionConstraint[] | undefined) {
    let nsource: SourcedValueOptions;
    if (source == null) {
      if (Array.isArray(value)) {
        nsource = value as Option[];
      } else {
        nsource = [value as Option];
      }
    } else {
      nsource = source;
    }

    super(nsource, optionAvailability);
    this.value = value;
  }

  hasSourceWithId(id: OptionId): boolean {
    return this.getSource().some(sourcedValue => sourcedValue._id === id);
  }
}

export class LazySourcedValue extends SourcedValue {
  protected initialized = false;

  constructor(value: Option | Option[],
              private readonly sourceOptionIds: OptionId[],
              private readonly sourceFn: () => SourcedValueOptions | null = null,
              optionAvailability?: ExpressionConstraint[] | undefined) {
    super(value, null, optionAvailability);
  }

  hasSourceWithId(id: OptionId): boolean {
    return this.sourceOptionIds.includes(id);
  }

  getSource(p1?: string): SourcedValueOptions {
    if (!this.initialized) {
      this.source = this.sourceFn();
      this.initialized = true;
    }
    return super.getSource(p1);
  }
}

export function sourcedValueGuard(v: unknown): v is SourcedValue {
  return typeof v === 'object' && v.hasOwnProperty('source') && Array.isArray(v['source']) && v.hasOwnProperty('value');
}
